import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClassParams,
  ColDef,
  ColGroupDef,
  ExcelCell,
  ExcelExportParams,
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { get, omit, uniq } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';

import { VendorDropdownComponent } from '@shared/components/vendor-dropdown/vendor-dropdown.component';
import { TableConstants } from '@shared/constants/table.constants';
import { NoopValueAccessorDirective } from '@shared/directives/noop-value-accessor.directive';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { Currency } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { QuarterCloseChecklistPeriodCloseService } from '@shared/services/quarter-close-checklist-period-close.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationStore } from '@shared/store/organization/organization.store';
import { AuxExcelStyles } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import {
  MonthCloseTableRowData,
  PeriodCloseComponent,
  QuarterDate,
} from '../../period-close.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NoopValueAccessorDirective,
    VendorDropdownComponent,
    FormsModule,
    ReactiveFormsModule,
    ExportExcelButtonComponent,
    AgGridAngular,
    StickyGridDirective,
    AsyncPipe,
  ],
  selector: 'aux-reconciliation',
  styles: [
    `
      :host ::ng-deep aux-vendor-dropdown .ng-dropdown-panel {
        left: -150px;
      }
    `,
  ],
  templateUrl: './reconciliation.component.html',
})
export class ReconciliationComponent {
  organizationQuery = inject(OrganizationQuery);
  public periodCloseComponent = inject(PeriodCloseComponent);
  public periodCloseService = inject(QuarterCloseChecklistPeriodCloseService);

  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private organizationStore = inject(OrganizationStore);

  filteredGridData$ = combineLatest([
    this.periodCloseComponent.gridData$,
    this.organizationQuery.selectActiveId(),
  ]).pipe(
    map(([data, vendorId]) => {
      if (!vendorId) {
        return data;
      }

      return data.filter((row) => row.vendor_id === vendorId);
    })
  );

  defaultColumns: (ColDef | ColGroupDef)[] = [
    {
      field: 'vendor_name',
      headerName: 'Vendor Contract',
      hide: true,
      rowGroup: true,
    },
    {
      field: 'vendor_id',
      headerName: 'Vendor Contract',
      hide: true,
    },
    {
      field: 'cost_category',
      headerName: 'Cost Category',
      hide: true,
    },
  ];
  excelOptions = {
    columnWidth(params) {
      switch (params.column?.getId()) {
        case 'activity_name':
          return 490;
        case 'vendor_name':
          return 280;
        default:
          return 105;
      }
    },
    fileName: 'auxilius-budget.xlsx',
    sheetName: 'Budget',
    shouldRowBeSkipped(params) {
      return !params?.node?.data?.cost_category;
    },
  } as ExcelExportParams;
  gridAPI!: GridApi<MonthCloseTableRowData>;
  gridAPI$: ReplaySubject<GridApi> = new ReplaySubject<GridApi>(1);
  gridOptions$ = new BehaviorSubject<GridOptions | null>(null);
  selectedVendor = new UntypedFormControl('');
  showAnalyticsSection$: Observable<boolean>;
  vendorCurrencies: Currency[] = [];

  autoSize() {
    this.gridAPI.sizeColumnsToFit();
  }

  // eslint-disable-next-line max-lines-per-function
  constructor() {
    this.showAnalyticsSection$ = this.launchDarklyService.select$(
      (flags) => flags.section_reconciliation_analytics
    );

    combineLatest([
      this.periodCloseComponent.budgetQuery.select(),
      this.periodCloseComponent.selectedQuarter.valueChanges.pipe(
        startWith(this.periodCloseComponent.selectedQuarter.value as string)
      ),
      this.periodCloseComponent.quartersObjUpdated$.pipe(startWith(null)),
    ])
      .pipe(
        // eslint-disable-next-line max-lines-per-function
        map(([{ budget_info }]) => {
          this.vendorCurrencies = uniq(budget_info.map((x) => x.vendor_currency as Currency));
          const startingQuarter = this.periodCloseComponent.selectedQuarter.value || '';

          this.periodCloseService.selectedQuarterMonthChanged$.next(null);
          const months = this.periodCloseComponent.quartersObj[startingQuarter];

          const current_month = budget_info?.[0]?.current_month;

          if (!current_month || !months) {
            return [];
          }

          const getMonthHeader = (m: QuarterDate) => {
            return m.parsedDate.format('MMM');
          };

          const endingBalanceColumn = {
            children: [
              {
                aggFunc: Utils.agSumFunc,
                cellClass: this.getCellClass(),
                field: 'eom_accruals.total',
                headerName: ' ',
                minWidth: 150,
                tooltipField: 'endingBalance',
                valueFormatter: this.currencyFormatter,
              },
            ],
            field: 'endingBalance',
            headerClass: 'ag-header-align-center balance',
            headerName: 'Ending Balance',
            valueFormatter: this.currencyFormatter,
          };

          const columns = [
            {
              children: months.map((month) => {
                return {
                  aggFunc: Utils.agSumFunc,
                  cellClass: this.getCellClass(),
                  field: `accrual_adjusted_obj.${month.date}`,
                  headerClass: 'ag-header-align-center',
                  headerName: getMonthHeader(month),
                  minWidth: 100,
                  valueFormatter: this.currencyFormatter,
                };
              }),
              headerClass: 'ag-header-align-center in-month-accrual',
              headerName: 'Work Performed Estimate',
              minWidth: 180,
            },
            {
              children: months.map((month) => {
                return {
                  aggFunc: Utils.agSumFunc,
                  cellClass: this.getCellClass(),
                  field: `invoice_amounts.${month.date}`,
                  headerClass: 'ag-header-align-center',
                  headerName: getMonthHeader(month),
                  minWidth: 100,
                  valueFormatter: this.currencyFormatter,
                };
              }),
              headerClass: 'ag-header-align-center invoiced',
              headerName: 'Invoices Received',
              maxWidth: 240,
            },
            {
              children: months.map((month) => {
                return {
                  aggFunc: Utils.agSumFunc,
                  cellClass: this.getCellClass(),
                  field: `net_accruals.${month.date}`,
                  headerClass: 'ag-header-align-center',
                  headerName: getMonthHeader(month),
                  minWidth: 100,
                  valueFormatter: this.currencyFormatter,
                };
              }),
              headerClass: 'ag-header-align-center in-month-accrual-net',
              headerName: 'Net Change in Accruals / (Prepaid)',
              minWidth: 180,
            },
            endingBalanceColumn,
          ];

          this.gridOptions$.next({
            ...this.gridOptions(),
            columnDefs: [...this.defaultColumns, ...columns],
            excelStyles: [
              ...AuxExcelStyles,
              ...Utils.generateExcelCurrencyStyles(Utils.CURRENCY_OPTIONS),
            ],
          });

          return [];
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  currencyFormatter = (params: ValueFormatterParams) => {
    let currency = this.vendorCurrencies[0];

    if (this.selectedVendor.value) {
      currency =
        this.organizationQuery.getVendor(this.selectedVendor.value)[0]?.currency ||
        this.vendorCurrencies[0];
    } else if (params.data && params.data.contract_direct_cost_currency) {
      currency = params.data.contract_direct_cost_currency;
    } else if (params.node?.key) {
      currency =
        this.organizationQuery.getAll().filter((x) => x.name === params.node?.key)[0]?.currency ||
        this.vendorCurrencies[0];
    }

    return Utils.agCurrencyFormatterAccounting(params, currency);
  };

  customColFilter = (cel: ColDef | ColGroupDef) => !!cel.headerName;

  firstDataRendered({ api }: { api: GridApi }) {
    api.sizeColumnsToFit();
  }

  getCellClass = () => (params: CellClassParams) => {
    if (params?.data?.contract_direct_cost_currency) {
      return [`budgetCost${params?.data?.contract_direct_cost_currency}`, 'ag-cell-align-right'];
    }

    return ['budgetCostNoSymbol', 'ag-cell-align-right'];
  };

  getDynamicExcelParams = (): ExcelExportParams => {
    let totalData:
      | undefined
      | {
          accrual_adjusted_obj: Record<string, number>;
          eom_accruals: Record<string, number>;
          invoice_amounts: Record<string, number>;
          net_accruals: Record<string, number>;
        } = undefined;
    let currencyForFormatting = Currency.USD;

    if (this.vendorCurrencies.length === 1 || this.selectedVendor.value) {
      if (this.vendorCurrencies.length === 1) {
        currencyForFormatting = this.vendorCurrencies[0];
      } else {
        currencyForFormatting =
          this.organizationQuery.getVendor(this.selectedVendor.value)[0]?.currency || Currency.USD;
      }

      totalData = this.getTotalRowData();
    }

    const trialName = this.mainQuery.getSelectedTrial()?.short_name;

    const rows = (this.gridAPI.getAllDisplayedColumns() || [])
      .filter((col) => {
        const columnName = col.getColId();

        return !!columnName && col.getColId() !== 'ag-Grid-AutoColumn';
      })
      .map<ExcelCell>((col) => {
        const columnName = col.getColId() as string;

        const val = get(totalData, columnName);

        return {
          data: { type: 'Number', value: `${val}` },
          styleId: `total_row_${currencyForFormatting}`,
        };
      });

    return {
      appendContent:
        this.vendorCurrencies.length === 1 || this.selectedVendor.value
          ? [
              {
                cells: [
                  {
                    data: { type: 'String', value: `Total` },
                    mergeAcross: 1,
                    styleId: `total_row_${currencyForFormatting}`,
                  },
                  ...rows,
                ],
              },
            ]
          : [],
      prependContent: [
        {
          cells: [
            {
              data: { type: 'String', value: `Trial: ${trialName}` },
              mergeAcross: rows.length + 1,
              styleId: 'first_row',
            },
          ],
        },
      ],
    };
  };

  getTotalRowData() {
    const rowsData: MonthCloseTableRowData[] = [];

    this.gridAPI?.forEachNodeAfterFilter((node) => {
      if (node.data) {
        rowsData.push(node.data);
      }
    });

    const setValueForMonth =
      (
        resultObj: Record<string, Record<string, number>>,
        rowValues: MonthCloseTableRowData,
        month: string
      ) =>
      (key: 'accrual_adjusted_obj' | 'invoice_amounts' | 'net_accruals'): void => {
        resultObj[key][month] = (resultObj[key][month] || 0) + (rowValues[key][month] || 0);
      };

    return rowsData.reduce(
      (accum, data) => {
        const month = Object.keys(omit(data.accrual_adjusted_obj, 'total'));

        month.forEach((m) => {
          const setValue = setValueForMonth(accum, data, m);

          setValue('accrual_adjusted_obj');
          setValue('invoice_amounts');
          setValue('net_accruals');
        });

        accum.eom_accruals.total += data.eom_accruals.total;

        return accum;
      },
      {
        accrual_adjusted_obj: {},
        eom_accruals: {
          total: 0,
        },
        invoice_amounts: {},
        net_accruals: {},
      } as {
        accrual_adjusted_obj: Record<string, number>;
        eom_accruals: Record<string, number>;
        invoice_amounts: Record<string, number>;
        net_accruals: Record<string, number>;
      }
    );
  }

  gridOptions: () => GridOptions = () => ({
    autoGroupColumnDef: {
      cellRendererParams: {
        suppressCount: true,
      },
      colId: 'activity_name',
      field: 'activity_name',
      headerName: 'Vendor Contract / Cost Category',
      minWidth: 200,
      tooltipField: 'activity_name',
    },
    columnDefs: [],
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      sortable: false,
    },
    excelStyles: [
      ...AuxExcelStyles,
      {
        font: { bold: true, color: '#000', fontName: 'Arial', size: 11 },
        id: 'header',
        interior: { color: '#98aebd', pattern: 'Solid', patternColor: '#999999' },
      },
      {
        font: { bold: true, color: '#FFFFFF', fontName: 'Arial', size: 11 },
        id: 'headerGroup',
        interior: { color: '#999999', pattern: 'Solid', patternColor: '#999999' },
      },
      {
        font: { fontName: 'Arial', size: 11 },
        id: 'cell',
      },
    ],
    groupDefaultExpanded: 1,
    suppressAggFuncInHeader: true,
    suppressCellFocus: true,
    suppressColumnVirtualisation: true,
  });

  onDataRendered(e: FirstDataRenderedEvent) {
    this.gridAPI = e.api;
    this.gridAPI$.next(e.api);
    const allColumnIds: string[] = [];

    this.gridAPI.getColumns()?.forEach((column) => {
      allColumnIds.push(column.getColId());
    });
    this.gridAPI.autoSizeColumns(allColumnIds, false);

    this.setBottomData(this.vendorCurrencies.length !== 1);
    this.gridAPI.sizeColumnsToFit();
  }

  onGridReady({ api }: GridReadyEvent) {
    api.sizeColumnsToFit();
  }

  onVendorSelected(vendorId: string) {
    this.organizationStore.setActive(vendorId || null);
    setTimeout(() => {
      this.setBottomData(!vendorId && this.vendorCurrencies.length !== 1);
    }, 0);
  }

  setBottomData(removeBottomRow: boolean) {
    if (removeBottomRow) {
      this.gridAPI?.setGridOption('pinnedBottomRowData', []);
    } else {
      this.gridAPI?.setGridOption('pinnedBottomRowData', [
        {
          activity_name: 'Total',
          ...this.getTotalRowData(),
        },
      ]);
    }
  }
}
